<template>
    <div>
        <h3 class="py-2">Marges sur ventes</h3>
        <v-row class="d-flex justify-center">
            <v-col cols="10">
                <v-data-table
                    :headers="salesMarginHeaders"
                    :items="salesMargins"
                    :loading="loading"
                    class="table"
                    fixed-header
                    :items-per-page="-1"
                    hide-default-footer
                    :multi-sort="false"
                    density="compact"
                >
                    <template #item="{ item }">
                        <tr>
                            <td>{{ item.product.title || "-" }}</td>
                            <td class="text-right">
                                {{ newCommonFormatCurrency(item.unitSalesPrice, undefined) }}
                            </td>
                            <td class="text-right">
                                {{ newCommonFormatCurrency(item.salesPrice, undefined) }}
                            </td>
                            <td class="text-right">
                                {{ newCommonFormatCurrency(item.directExpensePrice, undefined) }}
                            </td>
                            <td class="text-right">
                                {{ newCommonFormatCurrency(item.indirectExpensePrice, undefined) }}
                            </td>
                            <td class="text-right">
                                {{ newCommonFormatCurrency(item.expensePrice, undefined) }}
                            </td>
                            <td class="text-right">
                                {{ newCommonFormatCurrency(item.margin, undefined) }}
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>
        <h3 class="py-2">{{ resourceMarginTitle }}</h3>
        <v-row class="d-flex justify-center">
            <v-col cols="10">
                <v-data-table
                    :headers="resourceAllocationHeaders"
                    :items="resourceAllocationMargins"
                    :loading="loading"
                    class="table"
                    fixed-header
                    :items-per-page="-1"
                    hide-default-footer
                    :multi-sort="false"
                    density="compact"
                >
                    <template #headers>
                        <tr>
                            <th
                                v-for="(header, index) in resourceAllocationHeaders"
                                :key="index"
                                :class="`text-${header.align}`"
                            >
                                <div style="overflow-wrap: anywhere">
                                    <span style="font-size: 11pt">{{ header.title }}</span>
                                </div>
                                <div style="overflow-wrap: anywhere" v-if="header.measureUnit">
                                    <span style="font-size: 11pt">({{ header.measureUnit }})</span>
                                </div>
                            </th>
                        </tr>
                    </template>
                    <template #item="{ item }">
                        <tr>
                            <td>{{ item.name || "-" }}</td>
                            <td class="text-center">{{ mathFix(item.resourceQuantity, 0) }}</td>
                            <td class="text-center">{{ mathFix(item.yield, 2) }}</td>
                            <td class="text-right">
                                {{ newCommonFormatCurrency(item.unitSalesPrice, undefined) }}
                            </td>
                            <td class="text-right">
                                {{ newCommonFormatCurrency(item.salesPrice, undefined) }}
                            </td>
                            <td class="text-right">
                                {{ newCommonFormatCurrency(item.expensePrice, undefined) }}
                            </td>
                            <td class="text-right">
                                {{ newCommonFormatCurrency(item.margin, undefined) }}
                            </td>
                        </tr>
                    </template>
                    <template v-slot:body.append>
                        <tr class="x-dense">
                            <td class="text-left"><strong>Total</strong></td>
                            <td class="text-center">
                                {{ mathFix(totalResourceQuantity, 0) }}
                            </td>
                            <td class="text-right" />
                            <td class="text-right" />
                            <td class="text-right">
                                {{ newCommonFormatCurrency(totalResourceSalesPrice, undefined) }}
                            </td>
                            <td class="text-right">
                                {{ newCommonFormatCurrency(totalResourceExpensePrice, undefined) }}
                            </td>
                            <td class="text-right">
                                {{ newCommonFormatCurrency(totalResourceMargin, undefined) }}
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>
    </div>
</template>

<script lang="ts">
import { mathFix } from "../../services/mathServices";
import { newCommonFormatCurrency } from "../../../../backend/src/common/services/mathServices";
import { State2Way } from "@/decorators";
import { Apollo } from "../../decorators";
import { getDBFormattedEndDate, getDBFormattedStartDate } from "../../services/dateServices";

import COMPUTE_SALES_MARGINS from "../../graphql/stock/ComputeSalesMargins.gql";
import { Component, Vue, toNative } from "vue-facing-decorator";
import { Prop } from "vue-facing-decorator";

@Component
export class SalesMarginsVue extends Vue {
    @Prop({ default: "" }) companyId: string;

    @State2Way("updateAccountingPeriod", "accountingPeriod.selectedAccountingPeriod")
    selectedAccountingPeriod!: any;

    get salesMarginHeaders(): {
        title: string;
        value: string;
        sortable?: boolean;
        align?: "start" | "center" | "end";
        width?: string;
    }[] {
        return [
            { title: "Produit", value: "title", align: "start" },
            { title: "Prix unitaire", value: "unitSalesPrice", align: "end" },
            { title: "Prix de vente", value: "salesPrice", align: "end" },
            { title: "Charges directes prop.", value: "directExpensePrice", align: "end" },
            { title: "Charges indirectes prop.", value: "indirectExpensePrice", align: "end" },
            { title: "Charges prop.", value: "expensePrice", align: "end" },
            { title: "Marge", value: "margin", sortable: false, align: "end" },
        ];
    }

    get resourceAllocationHeaders(): {
        title: string;
        value: string;
        sortable?: boolean;
        align?: "start" | "center" | "end";
        width?: string;
        measureUnit: string;
    }[] {
        return [
            { title: "Ressource", value: "title", align: "start", measureUnit: undefined },
            {
                title: "Quantité de ressource",
                value: "resourceQuantity",
                align: "center",
                measureUnit: "Ha",
            },
            { title: "Rendement", value: "yield", align: "center", measureUnit: "T/Ha" },
            { title: "Prix unitaire", value: "unitSalesPrice", align: "end", measureUnit: "€/T" },
            // { title: "Prime", value: "bonus", align: 'end' },
            { title: "Prix de vente", value: "salesPrice", align: "end", measureUnit: "€/Ha" },
            { title: "Charges prop.", value: "expensePrice", align: "end", measureUnit: "€/Ha" },
            {
                title: "Marge",
                value: "margin",
                sortable: false,
                align: "end",
                measureUnit: "€/Ha",
            },
        ];
    }

    get loading() {
        if (this.$apollo.queries.output) {
            return this.$apollo.queries.output.loading;
        }
        return false;
    }

    newCommonFormatCurrency(value, options = undefined) {
        return newCommonFormatCurrency(value, undefined);
    }

    @Apollo({
        query() {
            return COMPUTE_SALES_MARGINS;
        },
        variables() {
            return {
                companyId: this.companyId,
                startDate: getDBFormattedStartDate(this.selectedAccountingPeriod.start),
                endDate: getDBFormattedEndDate(this.selectedAccountingPeriod.end),
            };
        },
        skip() {
            return !this.companyId || !this.selectedAccountingPeriod;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data && data.computeSalesMargins) {
                    this.output = data.computeSalesMargins.output;
                    if (this.output.resourceType) {
                        this.resourceAllocationHeaders[0].text = this.output.resourceType;
                    }
                } else {
                    this.output = null;
                }
            }
        },
    })
    output = null;

    get salesMargins() {
        if (this.output?.salesMargins?.length) {
            return this.output.salesMargins;
        }
        return [];
    }

    get resourceAllocationMargins() {
        if (this.output?.resourceAllocationMargins?.length) {
            return this.output.resourceAllocationMargins;
        }
        return [];
    }

    mathFix(value, dec) {
        return mathFix(value, dec);
    }

    //
    //
    //
    get resourceMarginTitle() {
        if (this.output?.resourceType) {
            let resourceType = this.output.resourceType;
            if (resourceType.endsWith("s")) {
                resourceType = resourceType.substring(0, resourceType.length - 1);
            }
            return "Marge brute par " + resourceType.toLowerCase() + " de vente";
        }
        if (this.output) {
            return "Marge brute par ressource de vente";
        }
    }
    get totalResourceQuantity() {
        let total = 0.0;
        for (const resourceAllocationMargin of this.resourceAllocationMargins) {
            total += resourceAllocationMargin.resourceQuantity;
        }
        return total;
    }

    get totalResourceSalesPrice() {
        let total = 0.0;
        for (const resourceAllocationMargin of this.resourceAllocationMargins) {
            total += resourceAllocationMargin.salesPrice;
        }
        return total;
    }

    get totalResourceExpensePrice() {
        let total = 0.0;
        for (const resourceAllocationMargin of this.resourceAllocationMargins) {
            total += resourceAllocationMargin.expensePrice;
        }
        return total;
    }

    get totalResourceMargin() {
        let total = 0.0;
        for (const resourceAllocationMargin of this.resourceAllocationMargins) {
            total += resourceAllocationMargin.margin;
        }
        return total;
    }
}

export default toNative(SalesMarginsVue);
</script>
